import { FC, useState, useMemo, useCallback } from 'react';
import { SortingState } from '@tanstack/react-table';
import { DataTable } from 'components/table/DataTable';
import { createCityFeesColumns } from './CityFeesColumns';
import { CityFeesFilters } from './CityFeesFilters';
import { CityFeeSheet } from './CityFeeSheet';
import { ConfirmDialog } from '../ConfirmDialog';
import { useCityFees } from 'src/hooks/useCityFees';
import { CityFee } from '@libs/schemas';

export const CityFeesDataTable: FC = () => {
  const [search, setSearch] = useState('');
  const [archivedFilter, setArchivedFilter] = useState<'all' | 'active' | 'archived'>('active');
  const [selectedCityFee, setSelectedCityFee] = useState<CityFee | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);

  // Confirmation dialog state
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [unarchiveDialogOpen, setUnarchiveDialogOpen] = useState(false);
  const [cityFeeToArchive, setCityFeeToArchive] = useState<CityFee | null>(null);
  const [cityFeeToUnarchive, setCityFeeToUnarchive] = useState<CityFee | null>(null);

  // Determine includeArchived based on filter
  const includeArchived = archivedFilter === 'all' || archivedFilter === 'archived';

  const { cityFees, isLoading, archiveFee, unarchiveFee, isArchiving, isUnarchiving } = useCityFees(
    {
      includeArchived,
      search,
    },
  );

  // Filter by archived status on the client side for 'archived' filter
  const filteredCityFees = useMemo(() => {
    if (archivedFilter === 'archived') {
      return cityFees.filter((fee) => fee.archived);
    }
    if (archivedFilter === 'active') {
      return cityFees.filter((fee) => !fee.archived);
    }
    return cityFees;
  }, [cityFees, archivedFilter]);

  const handleEditCityFee = useCallback((cityFee: CityFee) => {
    setSelectedCityFee(cityFee);
    setSheetOpen(true);
  }, []);

  // Archive handler - open confirmation dialog
  const handleArchiveCityFee = useCallback((cityFee: CityFee) => {
    setCityFeeToArchive(cityFee);
    setArchiveDialogOpen(true);
  }, []);

  // Unarchive handler - open confirmation dialog
  const handleUnarchiveCityFee = useCallback((cityFee: CityFee) => {
    setCityFeeToUnarchive(cityFee);
    setUnarchiveDialogOpen(true);
  }, []);

  // Actual archive action (called after confirmation)
  const confirmArchive = useCallback(() => {
    if (cityFeeToArchive?._id) {
      archiveFee(cityFeeToArchive._id);
      setCityFeeToArchive(null);
    }
  }, [cityFeeToArchive, archiveFee]);

  // Actual unarchive action (called after confirmation)
  const confirmUnarchive = useCallback(() => {
    if (cityFeeToUnarchive?._id) {
      unarchiveFee(cityFeeToUnarchive._id);
      setCityFeeToUnarchive(null);
    }
  }, [cityFeeToUnarchive, unarchiveFee]);

  const columns = useMemo(
    () => createCityFeesColumns(handleEditCityFee, handleArchiveCityFee, handleUnarchiveCityFee),
    [handleEditCityFee, handleArchiveCityFee, handleUnarchiveCityFee],
  );

  const handleRowClick = (cityFee: CityFee) => {
    handleEditCityFee(cityFee);
  };

  const handleSheetClose = (open: boolean) => {
    setSheetOpen(open);
    if (!open) {
      setSelectedCityFee(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p>Loading city fees...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <CityFeesFilters
        search={search}
        onSearchChange={setSearch}
        archivedFilter={archivedFilter}
        onArchivedFilterChange={setArchivedFilter}
      />

      <DataTable<CityFee, unknown>
        columns={columns}
        data={filteredCityFees}
        sorting={sorting}
        onSortingChange={setSorting}
        onRowClick={handleRowClick}
        hasNextPage={false}
        hasPreviousPage={false}
        onPageChange={() => {}}
        currentPage={1}
        pageCount={1}
        total={filteredCityFees.length}
        resourceName="city fees"
        offset={0}
        limit={filteredCityFees.length}
      />

      <CityFeeSheet open={sheetOpen} onOpenChange={handleSheetClose} cityFee={selectedCityFee} />

      {/* Archive Confirmation Dialog */}
      <ConfirmDialog
        open={archiveDialogOpen}
        onOpenChange={setArchiveDialogOpen}
        title="Archive City Fee"
        description={`Are you sure you want to archive the city fee for ${cityFeeToArchive?.city}, ${cityFeeToArchive?.state}? This will make it inactive but you can restore it later.`}
        confirmText="Archive"
        onConfirm={confirmArchive}
        variant="destructive"
        isLoading={isArchiving}
      />

      {/* Unarchive Confirmation Dialog */}
      <ConfirmDialog
        open={unarchiveDialogOpen}
        onOpenChange={setUnarchiveDialogOpen}
        title="Unarchive City Fee"
        description={`Are you sure you want to unarchive the city fee for ${cityFeeToUnarchive?.city}, ${cityFeeToUnarchive?.state}? This will make it active again.`}
        confirmText="Unarchive"
        onConfirm={confirmUnarchive}
        variant="default"
        isLoading={isUnarchiving}
      />
    </div>
  );
};
